import { Component, OnInit, ChangeDetectionStrategy, HostListener, inject } from '@angular/core';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { DecimalPipe, Location } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import Swal from 'sweetalert2';

import { MenuCommonComponent } from '@app/shared/menu-common/menu-common.component';
import { SearchProductComponent } from '@app/shared/search-product/search-product.component';
import { AlertService } from '@app/shared/alert.service';
import { RentalBillingService, serverMessage } from '@app/sales/rental-billing/rental-billing.service';
import type { RentalBillingParams } from '@app/sales/rental-billing/rental-billing.model';
import { businessDayOf, dayNoonIso, todayStr } from '@app/shared/time';
import { CardLinks, RentalProductsService } from './rental-products.service';
import {
  BILLABLE_PERIODS, BILLING_KEYS, DEFAULT_BILLING, RULE_HINTS, RentalCard, RentalLinked, RentalProductHead, RentalTier, SECU_BILLING, TaxRate,
  billingOf, cardPayload, nextGridDay, priceFaults, secuFaults,
} from './rental-products.model';

// Une grille de paliers : from = jour d'effet « AAAA-MM-JJ », null pour la grille sans date.
interface Grid { from: string | null; tiers: RentalTier[]; }

// Un onglet = un contrat de location en cours d'édition ; orig = empreinte du corps envoyé, pour savoir s'il y a quelque chose à enregistrer.
interface Tab {
  source: RentalCard;
  card: RentalCard;
  grids: Grid[];
  billing: RentalBillingParams;
  seq: number;
  origSeq: number;
  orig: string;
  replaces?: number;
  saving: boolean;
  error: string | null;
}

const clone = <T>(v: T): T => JSON.parse(JSON.stringify(v));

@Component({
  selector: 'app-rental-product',
  templateUrl: './rental-product.component.html',
  styleUrls: ['./rental-product.component.scss'],
  imports: [FormsModule, RouterLink, DecimalPipe, MatIconModule, MatTooltipModule, MatProgressSpinnerModule, MenuCommonComponent, SearchProductComponent],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: true,
})
export class RentalProductComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private service = inject(RentalProductsService);
  private billingService = inject(RentalBillingService);
  private alert = inject(AlertService);
  private location = inject(Location);

  readonly hints = RULE_HINTS;
  readonly DUPLICATE_HINT = 'Crée un nouveau contrat de location, copie exacte de celui affiché.\n'
    + 'Les modifications faites dans cette copie ne vaudront que pour cet article : il ne partagera plus ce contrat avec les autres.\n'
    + 'Les autres articles gardent le contrat d\'origine, inchangé.\n'
    + 'Rien n\'est enregistré avant « Créer le contrat » ; « Abandonner » annule la copie.';
  readonly SECU_FAULTS_HINT = 'Les contrôles Sécu du passage bloquent toute ligne d\'un dossier pris en charge (modèle de FSE) dont le contrat de location s\'écarte de ce jeu.';

  productId = 0;
  product: RentalProductHead | null = null;
  tabs: Tab[] = [];
  current = 0;
  loading = false;
  error: string | null = null;
  linkedNames = new Map<number, string>();
  taxes: TaxRate[] = [];
  periods: { id: number; name: string }[] = [];

  // Rattacher un contrat existant : le catalogue se charge à la demande.
  attachOpen = false;
  attachLoading = false;
  attachError: string | null = null;
  attachText = '';
  catalogue: RentalCard[] = [];

  ngOnInit(): void {
    this.service.getTaxes().subscribe({ next: (t) => (this.taxes = t ?? []) });
    this.billingService.getPeriodNames().subscribe({
      next: (names) => (this.periods = [...names].filter(([id]) => BILLABLE_PERIODS.includes(id)).map(([id, name]) => ({ id, name }))),
    });
    this.route.paramMap.subscribe((pm) => {
      const id = Number(pm.get('id')) || 0;
      const contract = Number(pm.get('contract')) || undefined;
      if (id !== this.productId || !this.product) {
        this.productId = id;
        this.load(contract);
      } else if (contract) {
        this.select(Math.max(0, this.tabs.findIndex((t) => t.card.id === contract)));
      }
    });
  }

  @HostListener('window:beforeunload', ['$event'])
  onBeforeUnload(e: BeforeUnloadEvent): void {
    if (this.tabs.some((t) => this.isDirty(t))) e.preventDefault();
  }

  // Les onglets modifiés et non enregistrés survivent à la relecture, sauf `drop` (celui qu'on vient d'enregistrer ou de retirer).
  load(selectId?: number, drop?: Tab): void {
    this.loading = true;
    this.error = null;
    this.service.getRentalProduct(this.productId).subscribe({
      next: (sheet) => {
        const keep = this.tabs.filter((t) => t !== drop && this.isDirty(t));
        this.product = sheet.product;
        this.linkedNames = new Map(Object.entries(sheet.linked_names ?? {}).map(([k, v]) => [Number(k), v ?? '']));
        this.tabs = [...sheet.cards.map((c) => keep.find((k) => k.card.id === c.id) ?? this.makeTab(c)), ...keep.filter((k) => !k.card.id)];
        const i = selectId ? this.tabs.findIndex((t) => t.card.id === selectId) : -1;
        if (selectId && i < 0) this.alert.warn(`Le contrat de location n° ${selectId} n'est pas posé sur cet article.`);
        this.current = i >= 0 ? i : 0;
        this.loading = false;
        this.syncUrl();
      },
      error: (e) => { this.loading = false; this.error = serverMessage(e); },
    });
  }

  private makeTab(c: RentalCard, replaces?: number): Tab {
    const card = clone(c);
    const byFrom = new Map<string, RentalTier[]>();
    for (const t of card.tiered_pricing) {
      const from = t.valid_from ? (businessDayOf(t.valid_from) ?? '') : '';
      byFrom.set(from, [...(byFrom.get(from) ?? []), t]);
    }
    if (!byFrom.has('')) byFrom.set('', []);
    const grids = [...byFrom.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([from, tiers]) => ({ from: from || null, tiers: tiers.sort((a, b) => a.starting_period - b.starting_period) }));
    const seq = card.seq ?? this.tabs.length + 1;
    const tab: Tab = { source: clone(c), card, grids, billing: billingOf(card), seq, origSeq: seq, orig: '', replaces, saving: false, error: null };
    tab.orig = card.id ? this.fingerprint(tab) : '';
    return tab;
  }

  get tab(): Tab | null { return this.tabs[this.current] ?? null; }

  // Le contrat tel qu'il partirait : grilles remises à plat, et toujours les 8 règles affichées, pour ne jamais dépendre des défauts du code.
  private flatten(t: Tab): RentalCard {
    const card = clone(t.card);
    card.tiered_pricing = t.grids.flatMap((g) => g.tiers.map((x) => ({ ...x, valid_from: g.from ? dayNoonIso(g.from) : null })));
    const data: Record<string, any> = { ...(card.data ?? {}) };
    delete data['billing'];
    const b: Record<string, any> = {};
    for (const k of BILLING_KEYS) if (t.billing[k] !== undefined && t.billing[k] !== null && (t.billing[k] as any) !== '') b[k] = t.billing[k];
    data['billing'] = b;
    card.data = Object.keys(data).length ? data : null;
    return card;
  }

  private fingerprint(t: Tab): string {
    return JSON.stringify({ c: cardPayload(this.flatten(t)), s: t.seq });
  }

  isDirty(t: Tab): boolean {
    return !t.card.id || this.fingerprint(t) !== t.orig;
  }

  select(i: number): void {
    this.current = i;
    this.attachOpen = false;
    this.syncUrl();
  }

  // L'adresse dit le contrat affiché (/sales/rental-product/<article>/<contrat>) ; replaceState ne recharge rien, les saisies en cours restent.
  private syncUrl(): void {
    const id = this.tab?.card.id;
    const url = `/sales/rental-product/${this.productId}` + (id ? `/${id}` : '');
    if (this.location.path() !== url) this.location.replaceState(url);
  }

  // ─── Contrats de location : nouveau, duplication, retrait, rattachement ───

  newCard(): void {
    const vat = this.taxes.find((t) => t.rate === 20 && t.is_active)?.id ?? 1;
    const card: RentalCard = {
      id: 0, name: this.product?.name ?? '', vat, period: 4, is_active: true,
      tiered_pricing: [{ starting_period: 1, uprice_taxx: 0 }], linked_products: [], data: { billing: { ...DEFAULT_BILLING } },
      seq: this.tabs.length + 1, products_count: 0, products: [],
    };
    this.tabs.push(this.makeTab(card));
    this.select(this.tabs.length - 1);
  }

  // La copie remplace le contrat partagé sur CET article seulement, dans la même transaction (clé replaces de l'évènement).
  duplicate(t: Tab): void {
    const copy = this.flatten(t);
    copy.id = 0;
    copy.name = `${copy.name} (${this.product?.name ?? 'article ' + this.productId})`;
    copy.products = [];
    copy.products_count = 0;
    copy.seq = t.seq;
    const tab = this.makeTab(copy, t.card.id);
    tab.billing = { ...t.billing };
    this.tabs.splice(this.current + 1, 0, tab);
    this.select(this.current + 1);
  }

  async discard(t: Tab): Promise<void> {
    if (this.isDirty(t) && !(await this.ask(t.card.id ? 'Abandonner les modifications de ce contrat ?' : 'Abandonner ce nouveau contrat ?'))) return;
    const i = this.tabs.indexOf(t);
    if (t.card.id) {
      this.tabs[i] = this.makeTab(t.source);
      return;
    }
    this.tabs.splice(i, 1);
    this.select(Math.max(0, Math.min(this.current, this.tabs.length - 1)));
  }

  private async ask(text: string, confirmButtonText = 'Oui'): Promise<boolean> {
    const r = await Swal.fire({ icon: 'question', text, showCancelButton: true, confirmButtonText, cancelButtonText: 'Non', width: '32em',
      didOpen: (popup) => popup.querySelector<HTMLElement>('.swal2-html-container')?.style.setProperty('white-space', 'pre-line') });
    return r.isConfirmed;
  }

  save(t: Tab): void {
    const faults = this.isLppPrice(t) ? [] : priceFaults(t.grids); // prix LPP : le champ est grisé et ignoré
    if (faults.length) {
      t.error = 'Enregistrement refusé : ' + faults.join(' · ');
      return;
    }
    const card = this.flatten(t);
    const links: CardLinks = {};
    if (!card.id || t.seq !== t.origSeq) links.link_products = [{ product: this.productId, seq: Number(t.seq) || undefined }];
    if (!card.id && t.replaces) links.replaces = t.replaces;
    t.saving = true;
    t.error = null;
    this.service.saveCard(card, links).subscribe((r) => {
      t.saving = false;
      if (!r.ok) { t.error = r.message; return; }
      this.alert.success(card.id ? 'Contrat de location enregistré' : 'Contrat de location créé et rattaché à l\'article');
      this.load(r.id, t);
    });
  }

  // Le contrat reste au catalogue et sur ses autres articles ; seul le rattachement à celui-ci disparaît.
  async unlink(t: Tab): Promise<void> {
    if (!t.card.id) return;
    const others = (t.source.products?.length ?? 1) - 1;
    const msg = `Retirer le contrat de location « ${t.source.name} » de cet article ?` + (others > 0 ? `\nIl reste sur ${others} autre(s) article(s).` : '\nIl reste au catalogue, rattaché à aucun article.')
      + (this.isDirty(t) ? '\nLes modifications non enregistrées du contrat sont abandonnées.' : '');
    if (!(await this.ask(msg, 'Retirer'))) return;
    t.saving = true;
    this.service.saveCard(t.source, { unlink_products: [this.productId] }).subscribe((r) => {
      t.saving = false;
      if (!r.ok) { t.error = r.message; return; }
      this.alert.success('Contrat de location retiré de l\'article');
      this.load(undefined, t);
    });
  }

  toggleAttach(): void {
    this.attachOpen = !this.attachOpen;
    if (this.attachOpen && !this.catalogue.length) {
      this.attachLoading = true;
      this.attachError = null;
      this.service.getCards().subscribe({
        next: (cards) => { this.catalogue = cards ?? []; this.attachLoading = false; },
        error: (e) => { this.attachLoading = false; this.attachError = serverMessage(e); },
      });
    }
  }

  get attachable(): RentalCard[] {
    const here = new Set(this.tabs.map((t) => t.card.id));
    const q = this.attachText.trim().toLowerCase();
    return this.catalogue.filter((c) => !here.has(c.id) && (!q || c.name.toLowerCase().includes(q) || String(c.id) === q
      || c.tiered_pricing.some((t) => (t.fse_lpp ?? '').startsWith(q))));
  }

  attach(c: RentalCard): void {
    this.attachError = null;
    this.service.saveCard(c, { link_products: [{ product: this.productId }] }).subscribe((r) => {
      if (!r.ok) { this.attachError = r.message; return; }
      this.alert.success('Contrat de location rattaché à l\'article');
      this.attachOpen = false;
      this.load(c.id);
    });
  }

  // ─── Prix ───

  addTier(g: Grid): void {
    const last = g.tiers[g.tiers.length - 1];
    g.tiers.push({ starting_period: last ? last.starting_period + 1 : 1, uprice_taxx: last?.uprice_taxx ?? 0, fse_lpp: last?.fse_lpp ?? null });
  }

  removeTier(g: Grid, i: number): void { g.tiers.splice(i, 1); }

  // Une grille datée redonne tous ses paliers : elle part d'une copie de la dernière grille.
  addGrid(t: Tab): void {
    const last = t.grids[t.grids.length - 1];
    t.grids.push({ from: nextGridDay(todayStr()), tiers: last.tiers.map((x) => ({ starting_period: x.starting_period, uprice_taxx: x.uprice_taxx, fse_lpp: x.fse_lpp ?? null })) });
  }

  removeGrid(t: Tab, g: Grid): void { t.grids.splice(t.grids.indexOf(g), 1); }

  sortGrids(t: Tab): void { t.grids.sort((a, b) => (a.from ?? '').localeCompare(b.from ?? '')); }

  // Taux actifs, plus celui que porte déjà le contrat s'il ne l'est plus.
  taxOptions(t: Tab): TaxRate[] { return this.taxes.filter((x) => x.is_active || x.id === Number(t.card.vat)); }

  vatRate(t: Tab): number | null { return this.taxes.find((x) => x.id === Number(t.card.vat))?.rate ?? t.card.vat_rate ?? null; }

  ttc(t: Tab, ht: number): number | null {
    const r = this.vatRate(t);
    return r === null || ht === null || ht === undefined ? null : Number(ht) * (1 + r / 100);
  }

  currentGridFrom(t: Tab): string | null {
    const today = todayStr();
    const past = t.grids.filter((g) => !g.from || g.from <= today);
    return past.length ? past[past.length - 1].from : null;
  }

  isLppPrice(t: Tab): boolean { return t.billing.price_source !== 'CARD'; }

  // ─── Règles ───

  setRule<K extends keyof RentalBillingParams>(t: Tab, k: K, v: RentalBillingParams[K]): void {
    t.billing = { ...t.billing, [k]: v };
  }

  setPauseClose(t: Tab, v: any): void {
    const n = v === '' || v === null || v === undefined ? undefined : Math.max(0, Math.trunc(Number(v)));
    this.setRule(t, 'pause_close_after_days', Number.isFinite(n as number) ? n : undefined);
  }

  // Jeu Sécu : la clôture d'une longue pause n'est pas fixée par la Sécu, elle garde la valeur d'avant (JP 28/10).
  applyPreset(t: Tab, preset: 'secu' | 'defaults'): void {
    t.billing = preset === 'secu' ? { ...SECU_BILLING, pause_close_after_days: t.billing.pause_close_after_days } : { ...DEFAULT_BILLING };
  }

  faults(t: Tab): string[] { return secuFaults(t.billing); }

  otherData(t: Tab): [string, string][] {
    return Object.entries(t.card.data ?? {}).filter(([k]) => k !== 'billing').map(([k, v]) => [k, typeof v === 'string' ? v : JSON.stringify(v)]);
  }

  // ─── Ventes associées ───

  addLinked(t: Tab, p: any): void {
    const id = Number(p?.ppv?.product ?? p?.id);
    if (!id) return;
    this.linkedNames.set(id, p?.ppv?.lnk_product?.name ?? p?.name ?? '');
    t.card.linked_products.push({ product_id: id, quantity: 1, billing_trigger: { type: 'event', event_name: 'delivery' } });
  }

  removeLinked(t: Tab, i: number): void { t.card.linked_products.splice(i, 1); }

  triggerOf(l: RentalLinked): string {
    return l.billing_trigger.type === 'recurring' ? 'recurring' : (l.billing_trigger.event_name ?? 'delivery');
  }

  setTrigger(l: RentalLinked, v: string): void {
    if (v === 'recurring') return;
    l.billing_trigger = { type: 'event', event_name: v as 'delivery' | 'return' };
  }

  tabLabel(t: Tab): string { return t.card.name || 'Nouveau contrat'; }
}
